import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, RotateCcw, Save, Sparkles, X as XIcon } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { DiffField } from '@/components/ui/DiffField';
import { Dialog } from '@/components/ui/Dialog';
import { Drawer } from '@/components/ui/Drawer';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { TimeRange } from '@/components/ui/TimeRange';
import { WeekdayToggle } from '@/components/ui/WeekdayToggle';
import { useParseWindowFromNotes, useUpdateCustomer } from '@/hooks/useCustomers';
import {
  findActiveOverride,
  useParseOverrides,
  useResetParseOverride,
  useSetParseOverride,
  type ParseOverride,
  type ParseOverrideDuration,
  type ParseOverrideField,
} from '@/hooks/useParseOverrides';
import { formatDate } from '@/lib/format';
import type {
  Customer,
  MaintenanceWindowSpec,
  MaintenanceWindowSpecFrequency,
} from '@/types/api';

type DiffFieldKey = ParseOverrideField;
const DIFF_FIELDS: DiffFieldKey[] = ['frequency', 'weekdays', 'time_start', 'time_end'];

const WEEKDAY_LABELS: Record<number, string> = {
  0: 'Mo',
  1: 'Di',
  2: 'Mi',
  3: 'Do',
  4: 'Fr',
  5: 'Sa',
  6: 'So',
};

/**
 * Wartungsfenster-Tab pro Kunde (WT-205, Sprint 2 + Carry-Over Sprint 3).
 *
 * Erlaubt strukturierte Definition (frequency + weekdays + time-range),
 * persistiert über `PATCH /customers/{id}` mit `maintenance_window_spec`.
 *
 * "Aus Notes parsen" liefert Heuristik-Vorschlag, User bestätigt /
 * verwirft - es wird NICHTS automatisch gespeichert.
 *
 * Sprint-3-Carry-Over (WT-205-Followup):
 *  - Reject pro Feld ist persistent: POST /customers/{id}/parse-overrides mit
 *    {field, rejected_value, days}. Solange ein Override aktiv ist, blendet
 *    der Drawer das Diff-Pair aus, und das Hauptformular zeigt einen
 *    "Override aktiv bis ..."-Badge mit Reset-Button.
 */

// on_demand: vertragslose Kunden sind nicht zwangsweise monatlich fällig
// (Spec S8-06, Nachtrag N2); seit gen:types Teil des generierten Enums.
const FREQUENCIES: { value: MaintenanceWindowSpecFrequency; label: string }[] = [
  { value: 'monthly', label: 'monthly' },
  { value: 'quarterly', label: 'quarterly' },
  { value: 'biannual', label: 'biannual' },
  { value: 'yearly', label: 'yearly' },
  { value: 'on_demand', label: 'Auf Abruf (kein Zyklus)' },
  { value: 'custom', label: 'custom' },
];

const REJECT_DURATIONS: { days: ParseOverrideDuration; label: string }[] = [
  { days: 7, label: '7 Tage' },
  { days: 30, label: '30 Tage' },
  { days: 90, label: '90 Tage' },
  { days: -1, label: 'Nie wieder' },
];

interface Props {
  customer: Customer;
}

export function MaintenanceWindowTab({ customer }: Props): JSX.Element {
  // Der Server liefert bei jedem Refetch ein NEUES spec-Objekt (gleicher
  // Inhalt, andere Identität). Als Effect-Dependency würde das laufende
  // Eingaben zurücksetzen - daher auf den normalisierten Inhalt normieren.
  const initialKey = useMemo(
    () => JSON.stringify(normalizeSpec(customer.maintenance_window_spec ?? null)),
    [customer.maintenance_window_spec]
  );
  const [spec, setSpec] = useState<MaintenanceWindowSpec>(
    () => normalizeSpec(customer.maintenance_window_spec ?? null)
  );
  const [proposal, setProposal] = useState<MaintenanceWindowSpec | null>(null);
  const [proposalSnapshot, setProposalSnapshot] = useState<MaintenanceWindowSpec | null>(null);
  const [proposalRationale, setProposalRationale] = useState<string | null>(null);
  const [proposalConfidence, setProposalConfidence] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [staleWarning, setStaleWarning] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const update = useUpdateCustomer();
  const parse = useParseWindowFromNotes();

  const overridesQuery = useParseOverrides(customer.id);
  const overrides = overridesQuery.data?.data ?? [];
  const setOverride = useSetParseOverride();
  const resetOverride = useResetParseOverride();

  // Reset spec wenn Customer wechselt (z.B. URL-Navigation) oder der Server
  // einen inhaltlich anderen Stand liefert (z.B. nach erfolgreichem Save).
  useEffect(() => {
    setSpec(JSON.parse(initialKey) as MaintenanceWindowSpec);
    setProposal(null);
    setProposalSnapshot(null);
    setStaleWarning(false);
    setDrawerOpen(false);
    setToast(null);
  }, [customer.id, initialKey]);

  const onSave = async (): Promise<void> => {
    // Stale-Check: vor Save erneut /parse-window-from-notes aufrufen.
    // Wenn Diff zum Snapshot -> Warnung, kein Auto-Save.
    if (proposalSnapshot) {
      try {
        const res = await parse.mutateAsync(customer.id);
        if (
          JSON.stringify(normalizeSpec(res.suggestion)) !==
          JSON.stringify(normalizeSpec(proposalSnapshot))
        ) {
          setProposal(res.suggestion);
          setProposalRationale(res.rationale ?? null);
          setProposalConfidence(res.confidence);
          setStaleWarning(true);
          setDrawerOpen(true);
          return;
        }
      } catch {
        // Stale-Check best-effort - bei Fehler trotzdem speichern.
      }
    }
    update.mutate({
      customerId: customer.id,
      body: { maintenance_window_spec: normalizeSpec(spec) },
    });
  };

  const onParse = async (): Promise<void> => {
    const res = await parse.mutateAsync(customer.id);
    setProposal(res.suggestion);
    setProposalSnapshot(res.suggestion);
    setProposalRationale(res.rationale ?? null);
    setProposalConfidence(res.confidence);
    setStaleWarning(false);
    setDrawerOpen(true);
  };

  const acceptAll = (): void => {
    if (proposal) setSpec(proposal);
    setDrawerOpen(false);
  };

  const acceptOne = (field: DiffFieldKey): void => {
    if (!proposal) return;
    setSpec((cur) => mergeField(cur, proposal, field));
  };

  const showToast = (msg: string): void => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 4_000);
  };

  const onPersistReject = (
    field: DiffFieldKey,
    rejectedValue: unknown,
    days: ParseOverrideDuration
  ): void => {
    setOverride.mutate(
      { customerId: customer.id, field, rejectedValue, days },
      {
        onSuccess: () => {
          const label =
            days === -1
              ? 'wird nicht mehr gezeigt'
              : `wird die nächsten ${days} Tage nicht mehr gezeigt`;
          showToast(`Vorschlag für ${diffFieldLabel(field)} ${label}.`);
        },
      }
    );
  };

  const onResetOverride = (field: DiffFieldKey): void => {
    resetOverride.mutate(
      { customerId: customer.id, field },
      {
        onSuccess: () => {
          showToast(`Override für ${diffFieldLabel(field)} entfernt.`);
        },
      }
    );
  };

  const dirty = JSON.stringify(normalizeSpec(spec)) !== initialKey;

  return (
    <Card
      title="Wartungsfenster"
      description="Granulare Definition - ergänzt das alte Enum-Feld (standard / evening / quarterly / ...)."
      actions={
        <>
          <Button
            variant="secondary"
            leftIcon={<Sparkles className="size-4" />}
            loading={parse.isPending}
            onClick={() => void onParse()}
          >
            Aus Notes parsen
          </Button>
          <Button
            leftIcon={<Save className="size-4" />}
            disabled={!dirty}
            loading={update.isPending || parse.isPending}
            onClick={() => void onSave()}
          >
            Speichern
          </Button>
        </>
      }
    >
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="mb-3 rounded-md border border-success/30 bg-success/5 p-3 text-sm text-success"
          data-testid="window-toast"
        >
          {toast}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FieldWithOverrideBadge
          field="frequency"
          override={findActiveOverride(overrides, 'frequency')}
          onResetOverride={() => onResetOverride('frequency')}
          resetting={resetOverride.isPending}
        >
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Frequenz</span>
            <select
              value={spec.frequency ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                setSpec((s) => {
                  const next = { ...s };
                  if (raw) next.frequency = raw as MaintenanceWindowSpecFrequency;
                  else delete next.frequency;
                  return next;
                });
              }}
              className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <option value="">– keine Angabe –</option>
              {FREQUENCIES.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
        </FieldWithOverrideBadge>

        <FieldWithOverrideBadge
          field="weekdays"
          override={findActiveOverride(overrides, 'weekdays')}
          onResetOverride={() => onResetOverride('weekdays')}
          resetting={resetOverride.isPending}
        >
          <WeekdayToggle
            label="Erlaubte Wochentage"
            value={spec.weekdays ?? []}
            onChange={(weekdays) => setSpec((s) => ({ ...s, weekdays }))}
            hint="Mo–So. Leer = keine Einschränkung."
          />
        </FieldWithOverrideBadge>

        <div className="md:col-span-2">
          <FieldWithOverrideBadge
            field="time_start"
            override={
              findActiveOverride(overrides, 'time_start') ??
              findActiveOverride(overrides, 'time_end')
            }
            onResetOverride={() => {
              if (findActiveOverride(overrides, 'time_start'))
                onResetOverride('time_start');
              if (findActiveOverride(overrides, 'time_end')) onResetOverride('time_end');
            }}
            resetting={resetOverride.isPending}
          >
            <TimeRange
              label="Zeit-Fenster"
              start={spec.time_start ?? null}
              end={spec.time_end ?? null}
              onChange={({ start, end }) =>
                setSpec((s) => {
                  const next = { ...s };
                  if (start) next.time_start = start;
                  else delete next.time_start;
                  if (end) next.time_end = end;
                  else delete next.time_end;
                  return next;
                })
              }
              hint="z.B. 18:00 – 22:00 für evening-only Kunden."
            />
          </FieldWithOverrideBadge>
        </div>
      </div>

      {update.isError && <ErrorAlert error={update.error} className="mt-3" />}
      {parse.isError && <ErrorAlert error={parse.error} className="mt-3" />}
      {setOverride.isError && <ErrorAlert error={setOverride.error} className="mt-3" />}
      {resetOverride.isError && (
        <ErrorAlert error={resetOverride.error} className="mt-3" />
      )}

      {proposal && !drawerOpen && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
          <Badge tone="primary">Vorschlag</Badge>
          {proposalConfidence != null && (
            <span className="text-xs text-muted">
              Confidence: {Math.round(proposalConfidence * 100)}%
            </span>
          )}
          <Button size="sm" variant="ghost" onClick={() => setDrawerOpen(true)}>
            Diff erneut öffnen
          </Button>
        </div>
      )}

      <WindowDiffDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        current={spec}
        proposal={proposal}
        rationale={proposalRationale}
        confidence={proposalConfidence}
        staleWarning={staleWarning}
        onAcceptField={acceptOne}
        onPersistReject={onPersistReject}
        onAcceptAll={acceptAll}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Override-Badge für das Hauptformular
// ---------------------------------------------------------------------------

interface FieldWithOverrideBadgeProps {
  field: ParseOverrideField;
  override: ParseOverride | null;
  onResetOverride: () => void;
  resetting: boolean;
  children: React.ReactNode;
}

function FieldWithOverrideBadge({
  field,
  override,
  onResetOverride,
  resetting,
  children,
}: FieldWithOverrideBadgeProps): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      {children}
      {override && (
        <div
          className="flex items-center gap-2 rounded-md border border-warning/30 bg-warning/5 px-2 py-1 text-xs text-warning"
          data-testid={`override-badge-${field}`}
        >
          <AlertTriangle className="size-3" aria-hidden="true" />
          <span className="flex-1">
            Override aktiv bis <strong>{formatDate(override.expires_at)}</strong>
          </span>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<RotateCcw className="size-3" />}
            onClick={onResetOverride}
            loading={resetting}
            data-testid={`override-reset-${field}`}
          >
            Override entfernen
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Diff-Drawer
// ---------------------------------------------------------------------------

interface DrawerArgs {
  open: boolean;
  onClose: () => void;
  current: MaintenanceWindowSpec;
  proposal: MaintenanceWindowSpec | null;
  rationale: string | null;
  confidence: number | null;
  staleWarning: boolean;
  onAcceptField: (field: DiffFieldKey) => void;
  onPersistReject: (
    field: DiffFieldKey,
    rejectedValue: unknown,
    days: ParseOverrideDuration
  ) => void;
  onAcceptAll: () => void;
}

function WindowDiffDrawer({
  open,
  onClose,
  current,
  proposal,
  rationale,
  confidence,
  staleWarning,
  onAcceptField,
  onPersistReject,
  onAcceptAll,
}: DrawerArgs): JSX.Element {
  // Pro Property: identisch / changed?
  const diff = useMemo(() => buildDiff(current, proposal), [current, proposal]);
  // Akzept-/Ablehn-Tracking lokal (pro Drawer-Lebenszyklus).
  const [acceptedFields, setAcceptedFields] = useState<Set<DiffFieldKey>>(() => new Set());
  const [rejectedFields, setRejectedFields] = useState<Set<DiffFieldKey>>(() => new Set());
  const [rejectModalField, setRejectModalField] = useState<DiffFieldKey | null>(null);

  useEffect(() => {
    if (open) {
      setAcceptedFields(new Set());
      setRejectedFields(new Set());
      setRejectModalField(null);
    }
  }, [open]);

  if (!proposal) {
    return (
      <Drawer open={open} onClose={onClose} title="Wartungsfenster: Diff">
        <p className="text-sm text-muted">Noch kein Vorschlag verfügbar.</p>
      </Drawer>
    );
  }

  const onConfirmReject = (field: DiffFieldKey, days: ParseOverrideDuration): void => {
    const proposed = proposal[field];
    onPersistReject(field, proposed ?? null, days);
    setRejectedFields((prev) => new Set(prev).add(field));
    setAcceptedFields((prev) => {
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
    setRejectModalField(null);
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title="Wartungsfenster: aktueller Wert vs. Vorschlag"
        description={
          <span className="flex flex-wrap items-center gap-2">
            {confidence != null && (
              <Badge tone="primary">Confidence {Math.round(confidence * 100)}%</Badge>
            )}
            {staleWarning && (
              <span className="inline-flex items-center gap-1 text-warning">
                <AlertTriangle className="size-3" />
                Vorschlag wurde gerade neu erzeugt – Notes haben sich geändert.
              </span>
            )}
            {rationale && <span className="text-xs text-muted">{rationale}</span>}
          </span>
        }
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Schließen
            </Button>
            <Button onClick={onAcceptAll}>Alles übernehmen</Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {DIFF_FIELDS.map((field) => {
            const d = diff[field];
            return (
              <DiffField
                key={field}
                label={diffFieldLabel(field)}
                current={renderFieldValue(field, d.current)}
                proposed={renderFieldValue(field, d.proposed)}
                changed={d.changed}
                accepted={acceptedFields.has(field)}
                rejected={rejectedFields.has(field)}
                onAccept={() => {
                  onAcceptField(field);
                  setAcceptedFields((prev) => new Set(prev).add(field));
                  setRejectedFields((prev) => {
                    const next = new Set(prev);
                    next.delete(field);
                    return next;
                  });
                }}
                onReject={() => setRejectModalField(field)}
              />
            );
          })}
        </div>
      </Drawer>

      <RejectDurationDialog
        field={rejectModalField}
        onClose={() => setRejectModalField(null)}
        onConfirm={onConfirmReject}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Reject-Modal
// ---------------------------------------------------------------------------

interface RejectDialogProps {
  field: DiffFieldKey | null;
  onClose: () => void;
  onConfirm: (field: DiffFieldKey, days: ParseOverrideDuration) => void;
}

function RejectDurationDialog({
  field,
  onClose,
  onConfirm,
}: RejectDialogProps): JSX.Element {
  if (!field) {
    return (
      <Dialog open={false} onClose={onClose} title="">
        <span />
      </Dialog>
    );
  }

  return (
    <Dialog
      open={Boolean(field)}
      onClose={onClose}
      title={`Vorschlag ablehnen: ${diffFieldLabel(field)}`}
      description="Wie lange soll dieser Vorschlag unterdrückt werden?"
      footer={
        <Button variant="ghost" leftIcon={<XIcon className="size-4" />} onClick={onClose}>
          Abbrechen
        </Button>
      }
    >
      <div className="flex flex-col gap-2" data-testid="reject-duration-options">
        {REJECT_DURATIONS.map((opt) => (
          <Button
            key={opt.days}
            variant="secondary"
            onClick={() => onConfirm(field, opt.days)}
            data-testid={`reject-duration-${opt.days}`}
          >
            {opt.label}
          </Button>
        ))}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Diff-Helpers
// ---------------------------------------------------------------------------

interface PerFieldDiff<T> {
  current: T;
  proposed: T;
  changed: boolean;
}

interface FullDiff {
  frequency: PerFieldDiff<MaintenanceWindowSpecFrequency | null | undefined>;
  weekdays: PerFieldDiff<number[] | null | undefined>;
  time_start: PerFieldDiff<string | null | undefined>;
  time_end: PerFieldDiff<string | null | undefined>;
}

function buildDiff(
  current: MaintenanceWindowSpec,
  proposal: MaintenanceWindowSpec | null
): FullDiff {
  const p: MaintenanceWindowSpec = proposal ?? {};
  return {
    frequency: {
      current: current.frequency,
      proposed: p.frequency,
      changed: (current.frequency ?? null) !== (p.frequency ?? null),
    },
    weekdays: {
      current: current.weekdays,
      proposed: p.weekdays,
      changed: !arrayEqual(current.weekdays ?? [], p.weekdays ?? []),
    },
    time_start: {
      current: current.time_start,
      proposed: p.time_start,
      changed: (current.time_start ?? null) !== (p.time_start ?? null),
    },
    time_end: {
      current: current.time_end,
      proposed: p.time_end,
      changed: (current.time_end ?? null) !== (p.time_end ?? null),
    },
  };
}

function diffFieldLabel(field: DiffFieldKey): string {
  switch (field) {
    case 'frequency':
      return 'Frequenz';
    case 'weekdays':
      return 'Wochentage';
    case 'time_start':
      return 'Beginn';
    case 'time_end':
      return 'Ende';
  }
}

function renderFieldValue(field: DiffFieldKey, value: unknown): string {
  if (value == null) return '–';
  if (field === 'weekdays' && Array.isArray(value)) {
    if (value.length === 0) return '–';
    return value.map((n) => WEEKDAY_LABELS[n as number] ?? String(n)).join(', ');
  }
  // Defensiv: nur primitive Werte stringify-en. Objekte / Arrays liefern
  // sonst "[object Object]" und verwirren den Diff-Renderer.
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function mergeField(
  cur: MaintenanceWindowSpec,
  proposal: MaintenanceWindowSpec,
  field: DiffFieldKey
): MaintenanceWindowSpec {
  const next: MaintenanceWindowSpec = { ...cur };
  const value = proposal[field];
  if (value === undefined || value === null) {
    delete next[field];
  } else if (field === 'weekdays' && Array.isArray(value)) {
    next.weekdays = [...value].sort((a, b) => a - b);
  } else if (field === 'frequency') {
    next.frequency = value as MaintenanceWindowSpecFrequency;
  } else if (field === 'time_start') {
    next.time_start = value as string;
  } else if (field === 'time_end') {
    next.time_end = value as string;
  }
  return next;
}

function arrayEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort((x, y) => x - y);
  const sb = [...b].sort((x, y) => x - y);
  return sa.every((v, i) => v === sb[i]);
}

function normalizeSpec(spec: MaintenanceWindowSpec | null): MaintenanceWindowSpec {
  if (!spec) return {};
  const out: MaintenanceWindowSpec = {};
  if (spec.frequency) out.frequency = spec.frequency;
  if (spec.weekdays && spec.weekdays.length > 0) {
    out.weekdays = [...spec.weekdays].sort((a, b) => a - b);
  }
  if (spec.time_start) out.time_start = spec.time_start;
  if (spec.time_end) out.time_end = spec.time_end;
  return out;
}
